import TransferAutoCharge from "@/components/dashboard/TransferAutoCharge";
import useFetch from "@/hooks/useFetch";
import { fetchUserPaymentPercentage } from "@/services/paymentPercentage";
import { fetchCurrentUserUsersCount } from "@/services/users";
import useStore from "@/store";
import Link from "next/link";
import React, { useEffect } from "react";
import CountUp from "react-countup";
import { useTranslation } from "react-i18next";
import DebtStatusBadge from "../users1/DebtStatusBadge";
import LeakedFundsHistoryPopover from "../users1/LeakedFundsHistoryPopover";
import UserPaymentPercentage from "./UserPaymentPercentage";
import { Badge } from "../ui/Badge";
import { cn } from "@/lib/utils";

const StatisticsCard = ({
  title,
  value,
  href,
  icon,
  showAutoChargeDialog = false,
  isUsersCard = false,
  isDebtCard = false,
  commercialAssistant,
}) => {
  const { t } = useTranslation("stats");
  const showBalance = useStore(state => state.showBalance);
  const setUserPayment = useStore(state => state.setUserPayment);
  const { value: usersCounts } = useFetch(
    fetchCurrentUserUsersCount,
    [showBalance],
    isUsersCard && showBalance
  );
  value = isUsersCard ? usersCounts?.users : value;
  const hasDecimals = value % 1 !== 0;

  const { value: userPayment } = useFetch(
    fetchUserPaymentPercentage,
    [isDebtCard],
    isDebtCard
  );

  useEffect(() => {
    if (isDebtCard) {
      setUserPayment(userPayment);
    }
  }, [userPayment, isDebtCard, setUserPayment]);

  const LinkComponent = href ? Link : React.Fragment;

  return (
    <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
      <LinkComponent passHref href={href}>
        <div
          className={cn("flex gap-4 relative flex-1", {
            "cursor-pointer": !!href,
          })}
        >
          <div className="bg-muted/50 p-4 rounded-md h-fit">{icon}</div>

          <div className="flex flex-col gap-1">
            <span className="text-lg font-semibold text-muted-foreground">
              {title}
            </span>

            <span className="text-2xl items-center flex max-[340px]:text-xl font-bold whitespace-nowrap">
              {showBalance ? (
                <CountUp
                  end={value}
                  duration={1}
                  separator=","
                  decimals={hasDecimals ? 2 : 0}
                  decimal="."
                />
              ) : (
                "*****"
              )}
              &nbsp;
              {isUsersCard ? (
                <Badge variant="success">
                  {(usersCounts?.activeUsers ?? 0)?.toLocaleString()}&nbsp;
                  {t("users:active_user")}
                </Badge>
              ) : (
                <span className="text-lg">{t("DA")}</span>
              )}
            </span>
          </div>
        </div>
      </LinkComponent>

      <div className="flex gap-1 ms-20 flex-col">
        {userPayment?.enabled && <UserPaymentPercentage />}
        {showAutoChargeDialog && <TransferAutoCharge />}
        {isDebtCard && commercialAssistant && (
          <LeakedFundsHistoryPopover
            trigger={
              <div className="cursor-pointer">
                <DebtStatusBadge />
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};

export default StatisticsCard;
